// frontend/app/components/lessonPlans/teachsense/TeachSenseSection.js
//
// The card chrome every TeachSense section is built from, so seven sections
// cannot drift into seven different cards.
//
// Deliberately the same radius, border, padding and uppercase section title as
// the class-insights cards above them: TeachSense is more of the same surface,
// not a second design bolted onto it.

import React from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { tint } from "../lessonPlanTheme";
import { displayName, initialsOf, isAnonymous, isInferredName } from "./studentIdentity";

/** Then "+N more" — the same cap the class-insights student lists use. */
export const MAX_ROWS = 10;

// Neutral wash for an avatar with no band or phase of its own. A hex rather
// than a token because tint() blends from one; it is the same slate the
// unanswered band is drawn in.
const NEUTRAL_HEX = "#64748b";

export const EmptyLine = ({ colors, text }) => (
  <Text style={[styles.empty, { color: colors.textMuted }]}>{text}</Text>
);

/**
 * One card. `isEmpty` is passed rather than inferred from `children`, because a
 * section that renders a conditional child hands down a single null and there
 * is no honest way to tell that apart from "nothing to say".
 */
export const Section = ({
  colors,
  isNarrow = false,
  icon,
  accent,
  title,
  subtitle,
  count = null,
  isEmpty = false,
  empty = "",
  children,
  testID,
}) => {
  const tone = accent || colors.textSecondary;

  return (
    <View
      testID={testID}
      style={[
        styles.card,
        isNarrow ? styles.cardNarrow : null,
        { backgroundColor: colors.panelBg, borderColor: colors.border },
      ]}
    >
      <View style={styles.header}>
        {icon ? (
          <MaterialCommunityIcons name={icon} size={15} color={tone} style={styles.headerIcon} />
        ) : null}
        <Text
          accessibilityRole="header"
          style={[styles.title, { color: colors.textSecondary }]}
        >
          {title}
        </Text>
        {count === null ? null : (
          <View style={[styles.count, { backgroundColor: colors.subtleBg }]}>
            <Text style={[styles.countText, { color: tone }]}>{String(count)}</Text>
          </View>
        )}
      </View>
      {subtitle ? (
        <Text style={[styles.subtitle, { color: colors.textMuted }]}>{subtitle}</Text>
      ) : null}
      {isEmpty ? <EmptyLine colors={colors} text={empty} /> : children}
    </View>
  );
};

/**
 * Loading, failed and not-yet-generated all render the same chrome around one
 * line of explanation, so the view never vanishes out from under the teacher.
 * Mirrors the panel's own state card rather than inventing a second one.
 */
export const StateCard = ({ colors, icon, tone, text, action, testID }) => (
  <View
    testID={testID}
    style={[styles.state, { backgroundColor: colors.panelBg, borderColor: colors.border }]}
  >
    {icon ? (
      <MaterialCommunityIcons name={icon} size={18} color={tone || colors.textMuted} />
    ) : (
      <ActivityIndicator size="small" color={colors.accentPrimary} />
    )}
    <Text style={[styles.stateText, { color: colors.textSecondary }]}>{text}</Text>
    {action}
  </View>
);

/**
 * A student, drawn the way the class-insights lists draw one: initials in a
 * tinted disc, the name beside it, an optional trailing value, and whatever
 * detail the section wants underneath. The email reaches accessibilityLabel
 * and nothing else.
 */
export const StudentRow = ({
  entry,
  colors,
  isDarkMode,
  accent,
  hex,
  trailing = null,
  detail = "",
  divided = false,
  children,
  testID,
}) => {
  const name = displayName(entry);
  const inferred = isInferredName(entry);
  const tone = accent || colors.textSecondary;

  return (
    <View
      testID={testID}
      accessibilityLabel={[name, entry?.student_email, detail].filter(Boolean).join(", ")}
      style={[styles.row, divided ? { borderTopWidth: 1, borderTopColor: colors.border } : null]}
    >
      <View style={[styles.avatar, { backgroundColor: tint(hex || NEUTRAL_HEX, isDarkMode, 1.6) }]}>
        <Text style={[styles.avatarText, { color: tone }]}>
          {isAnonymous(entry) ? "?" : initialsOf(name)}
        </Text>
      </View>
      <View style={styles.rowBody}>
        <View style={styles.rowHead}>
          <Text
            numberOfLines={1}
            style={[
              styles.name,
              { color: inferred ? colors.textSecondary : colors.textPrimary },
            ]}
          >
            {name}
          </Text>
          {trailing}
        </View>
        {children}
      </View>
    </View>
  );
};

/** A number with a caption, for the small signal grids. */
export const StatChip = ({ colors, label, value, accent }) => (
  <View style={[styles.chip, { backgroundColor: colors.subtleBg, borderColor: colors.border }]}>
    <Text style={[styles.chipValue, { color: accent || colors.textPrimary }]}>{String(value)}</Text>
    <Text style={[styles.chipLabel, { color: colors.textMuted }]}>{label}</Text>
  </View>
);

/** Muted supporting line under a name or a prompt. */
export const MetaText = ({ colors, children, style }) => (
  <Text style={[styles.meta, { color: colors.textMuted }, style]}>{children}</Text>
);

const styles = StyleSheet.create({
  card: { borderRadius: 14, borderWidth: 1, padding: 20, marginBottom: 16 },
  cardNarrow: { padding: 14 },

  header: { flexDirection: "row", alignItems: "center", marginBottom: 10 },
  headerIcon: { marginRight: 7 },
  title: {
    fontSize: 12,
    fontWeight: "800",
    lineHeight: 16,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    flexShrink: 1,
  },
  count: {
    borderRadius: 999,
    minWidth: 22,
    paddingHorizontal: 7,
    paddingVertical: 1,
    alignItems: "center",
    marginLeft: 8,
  },
  countText: { fontSize: 11, fontWeight: "800" },
  subtitle: { fontSize: 12, fontWeight: "500", lineHeight: 17, marginTop: -4, marginBottom: 10 },

  row: { flexDirection: "row", paddingVertical: 10 },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    marginTop: 1,
  },
  avatarText: { fontSize: 11, fontWeight: "800" },
  rowBody: { flex: 1, minWidth: 0 },
  rowHead: { flexDirection: "row", alignItems: "baseline", gap: 8 },
  name: { flex: 1, fontSize: 14, fontWeight: "600", lineHeight: 19 },

  chip: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 74,
  },
  chipValue: { fontSize: 15, fontWeight: "800", lineHeight: 20, fontVariant: ["tabular-nums"] },
  chipLabel: { fontSize: 10, fontWeight: "600", lineHeight: 14, marginTop: 1 },

  meta: { fontSize: 11, fontWeight: "500", lineHeight: 16, marginTop: 2 },
  empty: { fontSize: 13, lineHeight: 19, fontStyle: "italic" },

  state: {
    borderRadius: 14,
    borderWidth: 1,
    padding: 20,
    minHeight: 96,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  stateText: { fontSize: 13, lineHeight: 19, flex: 1 },
});
